//
// Module for communicating with Raumtaenzer's backend services
//

const rp = require('request-promise').defaults({jar: true})
// import * as rp from 'request-promise';

const CMS_BASE_URL = "https://rt-ar-app.ar-cms.de"
const CALLSERVICE_URL = "https://flux-360-call-server.ar-cms.de:443" // "https://flux-360-call-server.ar-cms.de:8002"

/**
 * Take uri as relative path and return complete url to
 * CMS backend.
 * @param uri
 */
export function cmsUrl (uri) {
  return `${CMS_BASE_URL}${uri}`;
}

/**
 * Make a request asynchronously.
 *
 * @param options
 * @logToConsole {boolean} Log request to console? Defaults to <code>true</code>
 * @return {Promise<*>}
 */
async function makeRequest (options, logToConsole = true) {
  if (logToConsole) {
    console.debug(`Making request ${JSON.stringify(options, null)}`);
  }
  let res = null;
  try {
    res = await rp(options);
  } catch(e ) {
    // Safari is always throwing type errors when connecting the ar-cms
    console.log("Error: " + e);
  }
  if (logToConsole) {
    console.debug(`Received response ${JSON.stringify(res, null)}`);
  }
  return res;
}

/**
 * Take the given json response and make sure that the
 * 'success' flag is true. If not, create an error from
 * the 'description' field. Only for calls that follow
 * the scheme (success flag etc.)
 *
 * @param jsonResponse {Object} Parsed JSON response
 * @throws {Error}
 */
const assertSuccess = (jsonResponse) => {
  const success = JSON.parse(jsonResponse.success);
  if (!success) {
    const error = new Error(jsonResponse.description);
    if (jsonResponse.name) {
      error.name = jsonResponse.name;
    }
    if (jsonResponse.code) {
      error.code = jsonResponse.code;
    }
    throw error;
  }
}

/**
 * Log in with the CMS.
 *
 * @param email
 * @param password
 * @return {Promise<*>}
 */
export async function login (email, password) {
  const response = await makeRequest({
    method: 'POST',
    uri: `${CMS_BASE_URL}/flux/api/login`,
    form: {
      'mail': email,
      'password': password
    },
    json: true
  });
  assertSuccess(response);
  return response.content;
}

/**
 * Log off from CMS.
 *
 * @param accessToken
 * @return {Promise<*>}
 */
export async function logout (accessToken) {
  return await makeRequest({
    method: 'POST',
    headers: {
      "x-auth-token": accessToken
    },
    uri: `${CMS_BASE_URL}/flux/api/logout`,
    json: true
  });
}

export async function getRooms (jwt, userId) {
  console.debug(`making call with ${jwt} for ${userId}`);
  // TODO: replace with API call
  return [
    {
      id: 103591,
      name: "Reception"
    },
    {
      id: 103592,
      name: "White"
    },
    {
      id: 103593,
      name: "Clivia"
    },
    {
      id: 103594,
      name: "Vigour Vogue"
    },
  ]
}

/**
 * Get access data to join a tour video session.
 *
 * @param jwt {string} JWT auth token
 * @param data {Object} Necessary information of the structure:
 * <code>
 * {
 *      user_id: {string}
 *      user_name: {string},
 *      role: {string},
 *      room_id: {string}
 * }
 * </code>
 * @return {Promise<*>}
 */
export async function getVideoSessionInfoForRoom (jwt, data) {
  const response = await makeRequest({
    method: 'GET',
    uri: `${CALLSERVICE_URL}/request_session_connection`,
    qs: data,
    json: true
  });
  assertSuccess(response);
  return response;
}

/**
 * Sends a heartbeat to the call-service to inform that the given user
 * is still online.
 * @param userId {string} The user_id field given to this OpenTok user
 * when calling @see {getVideoSessionInfoForRoom}
 * @return {Promise<void>}
 */
export async function heartbeat (userId) {
  await makeRequest({
    method: 'POST',
    uri: `${CALLSERVICE_URL}/check_for_call?callee=${userId}`,
    json: true
  }, false);
}

/**
 * Obtain information on the users connected to the rooms listed.
 * The rooms are identified by their room ids as entered in the call
 * to @see #getVideoSessionInfoForRoom
 *
 * @param roomIds {Array<{String}>} room ids
 * @return {Array<Object>}
 */
export async function getSessionInfo (roomIds) {
  const response = await makeRequest({
    method: 'POST',
    uri: `${CALLSERVICE_URL}/get_session_info`,
    body: {
      room_ids: roomIds
    },
    json: true
  }, true);
  if(!response)
    return [];
  assertSuccess(response);
  return response.rooms;
}

/**
 * Retrieves contact status for the given list of users.
 *
 * @param callerId {string} user_id of the requesting party
 * @param contactIds {Array<String>} user_ids of the contacts of interest.
 * @return {Array<Object>} List of contact infos.
 */
export async function getOnlineStatus (callerId, contactIds) {
  const response = await makeRequest({
    method: 'POST',
    uri: `${CALLSERVICE_URL}/request_contact_status`,
    body: {
      id: callerId,
      contact_ids: contactIds
    },
    json: true
  }, true);
  if( !response )
    return [];
  assertSuccess(response);
  return response.contacts;
}